import React, { useState, useEffect } from 'react'
import {
  Box,
  Typography,
  Alert,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Checkbox,
  CircularProgress,
  Chip,
} from '@mui/material'
import {
  KeyboardArrowUp as ArrowUpIcon,
  KeyboardArrowDown as ArrowDownIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material'
import { assessmentsAPI } from '../../../services/api'

/**
 * Assessment track type configuration component
 * Rendered within the Agent Config section for assessment tracks
 */
function AssessmentConfig({ config = {}, onChange, trackCode }) {
  const [assessments, setAssessments] = useState([])
  const [loading, setLoading] = useState(true)

  const selectedCodes = config.assessment_codes || []

  useEffect(() => {
    fetchAssessments()
  }, [])

  const fetchAssessments = async () => {
    try {
      const response = await assessmentsAPI.getAssessments()
      setAssessments(response.data || [])
    } catch (err) {
      console.error('Error loading assessments:', err)
      setAssessments([])
    } finally {
      setLoading(false)
    }
  }

  const updateSelectedCodes = (newCodes) => {
    onChange({ assessment_codes: newCodes })
  }

  const toggleAssessment = (code) => {
    if (selectedCodes.includes(code)) {
      updateSelectedCodes(selectedCodes.filter((c) => c !== code))
    } else {
      updateSelectedCodes([...selectedCodes, code])
    }
  }

  const moveUp = (index) => {
    if (index === 0) return
    const newCodes = [...selectedCodes]
    ;[newCodes[index - 1], newCodes[index]] = [newCodes[index], newCodes[index - 1]]
    updateSelectedCodes(newCodes)
  }

  const moveDown = (index) => {
    if (index === selectedCodes.length - 1) return
    const newCodes = [...selectedCodes]
    ;[newCodes[index], newCodes[index + 1]] = [newCodes[index + 1], newCodes[index]]
    updateSelectedCodes(newCodes)
  }

  const removeAssessment = (code) => {
    updateSelectedCodes(selectedCodes.filter((c) => c !== code))
  }

  const getAssessmentByCode = (code) => {
    return assessments.find((a) => a.code === code)
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
        <CircularProgress size={20} />
      </Box>
    )
  }

  const selectedAssessments = selectedCodes
    .map((code) => getAssessmentByCode(code))
    .filter(Boolean)

  const unselectedAssessments = assessments.filter(
    (a) => !selectedCodes.includes(a.code)
  )

  return (
    <Box>
      <Typography variant="subtitle2" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: '0.05em', fontSize: '0.6875rem', mb: 0.5 }}>
        Assessment Configuration
      </Typography>
      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
        Select instruments and set their delivery order.
      </Typography>

      {/* Selected Assessments (Ordered) */}
      {selectedAssessments.length > 0 && (
        <Box sx={{ mb: 1.5 }}>
          <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
            Selected ({selectedAssessments.length}) — drag to reorder
          </Typography>
          <List disablePadding dense>
            {selectedAssessments.map((assessment, index) => (
              <ListItem
                key={assessment.code}
                disableGutters
                sx={{
                  bgcolor: 'action.hover',
                  borderRadius: 1,
                  mb: 0.5,
                  px: 1,
                  py: 0.25,
                }}
              >
                <Chip
                  label={index + 1}
                  size="small"
                  sx={{ mr: 1.5, minWidth: 24, height: 20, fontSize: '0.6875rem' }}
                />
                <ListItemText
                  primary={
                    <Typography variant="body2" fontSize="0.8125rem">
                      {assessment.code} — {assessment.name}
                    </Typography>
                  }
                  secondary={
                    <Typography variant="caption" color="text.secondary" fontSize="0.6875rem">
                      {assessment.questions?.length || 0} questions
                    </Typography>
                  }
                />
                <Box sx={{ display: 'flex', alignItems: 'center', ml: 'auto', flexShrink: 0 }}>
                  <IconButton
                    size="small"
                    onClick={() => moveUp(index)}
                    disabled={index === 0}
                    sx={{ p: 0.25 }}
                  >
                    <ArrowUpIcon fontSize="small" />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => moveDown(index)}
                    disabled={index === selectedAssessments.length - 1}
                    sx={{ p: 0.25 }}
                  >
                    <ArrowDownIcon fontSize="small" />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => removeAssessment(assessment.code)}
                    sx={{ p: 0.25, ml: 0.5 }}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Box>
              </ListItem>
            ))}
          </List>
        </Box>
      )}

      {selectedCodes.length === 0 && (
        <Alert severity="warning" sx={{ mb: 1.5, py: 0 }}>
          At least one assessment instrument must be selected.
        </Alert>
      )}

      {/* Available Assessments */}
      <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
        Available — click to add
      </Typography>
      {unselectedAssessments.length === 0 ? (
        <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
          All available assessments have been selected.
        </Typography>
      ) : (
        <List disablePadding dense>
          {unselectedAssessments.map((assessment) => (
            <ListItem
              key={assessment.code}
              button
              onClick={() => toggleAssessment(assessment.code)}
              disableGutters
              sx={{
                borderRadius: 1,
                px: 1,
                py: 0.25,
                '&:hover': { bgcolor: 'action.hover' },
              }}
            >
              <Checkbox
                edge="start"
                checked={false}
                tabIndex={-1}
                disableRipple
                size="small"
                sx={{ p: 0.25, mr: 1 }}
              />
              <ListItemText
                primary={
                  <Typography variant="body2" fontSize="0.8125rem">
                    {assessment.code} — {assessment.name}
                  </Typography>
                }
                secondary={
                  <Typography variant="caption" color="text.secondary" fontSize="0.6875rem">
                    {assessment.questions?.length || 0} questions
                  </Typography>
                }
              />
            </ListItem>
          ))}
        </List>
      )}
    </Box>
  )
}

export default AssessmentConfig
